import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge, fmtDate } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ChevronLeft, ChevronRight, Loader2, UserCog, Users, Tag, ShieldOff, Archive, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const VIEWS = [
  { key: "all", label: "All", params: {} },
  { key: "new", label: "New", params: { status: "new" } },
  { key: "unassigned", label: "Unassigned", params: { assigned: "unassigned" }, countKey: "unassigned" },
  { key: "followups", label: "Follow-ups Due", params: { followup: "due_today" } },
  { key: "interested", label: "Interested", params: { status: "interested" } },
  { key: "details-sent", label: "Details Sent", params: { status: "details-sent" } },
  { key: "webinar-ready", label: "Webinar Ready", params: { status: "webinar-ready" } },
  { key: "payment-done", label: "Payment Done", params: { status: "payment-done" } },
  { key: "booking-done", label: "Booking Done", params: { status: "booking-done" } },
  { key: "not-interested", label: "Not Interested", params: { status: "not-interested" } },
  { key: "lost", label: "Lost", params: { status: "lost" } },
];

export default function Leads() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [view, setView] = useState("all");
  const [q, setQ] = useState("");
  const [temperature, setTemperature] = useState("all");
  const [source, setSource] = useState("all");
  const [assignee, setAssignee] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [followup, setFollowup] = useState("all");
  const [dnc, setDnc] = useState("all");
  const [campaign, setCampaign] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [selected, setSelected] = useState(new Set());
  const [assignLead, setAssignLead] = useState(null);
  const [assignTo, setAssignTo] = useState("");
  const [bulk, setBulk] = useState(null);      // { action, target }
  const [busy, setBusy] = useState(false);

  const refreshMeta = useCallback(() => {
    api.get("/lead-statuses").then((r) => setStatuses(r.data.items)).catch(() => {});
    api.get("/employees").then((r) => setEmployees(r.data.items)).catch(() => {});
    api.get("/leads/counts").then((r) => setCounts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { refreshMeta(); }, [refreshMeta]);

  const [sp] = useSearchParams();
  useEffect(() => {
    const v = sp.get("view"), st = sp.get("status");
    if (v) setView(v);
    else if (st) { const m = VIEWS.find((x) => x.params.status === st); setView(m ? m.key : "all"); }
    if (sp.get("temperature")) setTemperature(sp.get("temperature"));
    if (sp.get("source")) setSource(sp.get("source"));
    if (sp.get("campaign")) setCampaign(sp.get("campaign"));
    if (sp.get("assigned_employee_id")) setAssignee(sp.get("assigned_employee_id"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const load = useCallback(async () => {
    const v = VIEWS.find((x) => x.key === view) || VIEWS[0];
    const params = { page, page_size: pageSize, ...v.params };
    if (q) params.q = q;
    if (temperature !== "all") params.temperature = temperature;
    if (source !== "all") params.source = source;
    if (assignee !== "all") params.assigned_employee_id = assignee;
    if (statusFilter !== "all") params.status = statusFilter;
    if (followup !== "all") params.followup = followup;
    if (dnc !== "all") params.is_dnc = dnc === "dnc";
    if (campaign) params.campaign = campaign;
    const { data } = await api.get("/leads", { params });
    setData(data);
    setSelected(new Set());
  }, [view, page, pageSize, q, temperature, source, assignee, statusFilter, followup, dnc, campaign]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const toggleSel = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const allOnPage = data?.items?.every((l) => selected.has(l.id));
  const toggleAll = () => setSelected((s) => {
    if (allOnPage) return new Set();
    return new Set(data.items.map((l) => l.id));
  });

  const doAssign = async () => {
    try {
      await api.post(`/leads/${assignLead.id}/assign`, { employee_id: assignTo || null, reason: "Manual admin assignment" });
      toast.success(assignTo ? "Lead assigned" : "Lead assigned via round-robin");
      setAssignLead(null); setAssignTo(""); load(); refreshMeta();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runBulk = async () => {
    setBusy(true);
    const ids = [...selected];
    try {
      let r;
      if (bulk.action === "assign") r = await api.post("/leads/bulk/assign", { lead_ids: ids, employee_id: bulk.target || null, reason: "Bulk assignment" });
      if (bulk.action === "status") r = await api.post("/leads/bulk/status", { lead_ids: ids, status_slug: bulk.target });
      if (bulk.action === "archive") r = await api.post("/leads/bulk/archive", { lead_ids: ids, value: true });
      if (bulk.action === "dnc") r = await api.post("/leads/bulk/dnc", { lead_ids: ids, value: true });
      const d = r.data;
      if (d.failed_count) toast.warning(`${d.updated} updated · ${d.failed_count} failed`);
      else toast.success(`${d.updated} leads updated`);
      setBulk(null); load(); refreshMeta();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const bulkTitle = bulk && {
    assign: `Reassign ${selected.size} lead(s)?`,
    status: `Change status of ${selected.size} lead(s)?`,
    archive: `Archive ${selected.size} lead(s)?`,
    dnc: `Mark ${selected.size} lead(s) as Do-Not-Call?`,
  }[bulk.action];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-heading text-3xl font-extrabold">Leads</h1>
        <p className="text-muted-foreground text-sm mt-1">{data?.total ?? 0} leads in this view · {counts?.total ?? 0} total.</p>
      </div>

      {/* Views */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {VIEWS.map((v) => (
          <button key={v.key} data-testid={`view-tab-${v.key}`} onClick={() => { setView(v.key); setPage(1); }}
            className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${view === v.key ? "bg-primary text-primary-foreground border-primary" : "border-slate-700 text-muted-foreground hover:text-foreground"}`}>
            {v.label}{v.countKey && counts ? ` · ${counts[v.countKey]}` : ""}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="glass rounded-xl p-4 grid md:grid-cols-6 gap-3">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search ID, name, mobile, email…" data-testid="lead-search-input" className="pl-9 bg-slate-900/60 border-slate-700" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setPage(1); setStatusFilter(v); }}>
          <SelectTrigger data-testid="filter-status" className="bg-slate-900/60 border-slate-700"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 max-h-72">
            <SelectItem value="all">All Statuses</SelectItem>
            {statuses.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={assignee} onValueChange={(v) => { setPage(1); setAssignee(v); }}>
          <SelectTrigger data-testid="filter-assignee" className="bg-slate-900/60 border-slate-700"><SelectValue placeholder="Telecaller" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all">All Telecallers</SelectItem>
            {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={followup} onValueChange={(v) => { setPage(1); setFollowup(v); }}>
          <SelectTrigger data-testid="filter-followup" className="bg-slate-900/60 border-slate-700"><SelectValue placeholder="Follow-up" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all">Any Follow-up</SelectItem>
            <SelectItem value="due_today">Due Today</SelectItem>
            <SelectItem value="upcoming">Upcoming</SelectItem>
            <SelectItem value="missed">Missed</SelectItem>
            <SelectItem value="none">No Follow-up</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dnc} onValueChange={(v) => { setPage(1); setDnc(v); }}>
          <SelectTrigger data-testid="filter-dnc" className="bg-slate-900/60 border-slate-700"><SelectValue placeholder="DNC" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="dnc">DNC only</SelectItem>
            <SelectItem value="non">Non-DNC</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Desktop table */}
      <div className="glass rounded-xl overflow-hidden hidden md:block">
        {!data ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-3"><Checkbox checked={allOnPage && data.items.length > 0} onCheckedChange={toggleAll} data-testid="select-all" /></th>
                  <th className="px-3 py-3">Lead ID</th>
                  <th className="px-3 py-3">Customer</th>
                  <th className="px-3 py-3">Status &amp; Remark</th>
                  <th className="px-3 py-3">Source</th>
                  <th className="px-3 py-3">Assigned</th>
                  <th className="px-3 py-3">Follow-up</th>
                  <th className="px-3 py-3">Created</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((l) => {
                  const sm = statuses.find((s) => s.slug === l.current_status);
                  return (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggleSel(l.id)} data-testid={`select-lead-${l.id}`} />
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-primary cursor-pointer" onClick={() => navigate(`/admin/leads/${l.id}`)}>{l.lead_code || l.lead_number}</td>
                      <td className="px-3 py-3 cursor-pointer" onClick={() => navigate(`/admin/leads/${l.id}`)}>
                        <div className="font-medium flex items-center gap-1.5">{l.name}{l.is_dnc && <ShieldOff className="h-3 w-3 text-red-400" />}{l.priority === "URGENT" && <span className="text-[10px] text-red-400">URGENT</span>}</div>
                        <div className="text-xs text-muted-foreground font-mono">{l.mobile}</div>
                      </td>
                      <td className="px-3 py-3">
                        {sm ? <StatusBadge status={sm.name} colorToken={sm.color_token} /> : l.current_status}
                        {l.latest_remark && <div data-testid={`lead-remark-${l.id}`} className="text-[11px] text-slate-400 mt-1 max-w-[220px] truncate">{l.latest_remark}</div>}
                      </td>
                      <td className="px-3 py-3 text-xs text-muted-foreground">{l.source}</td>
                      <td className="px-3 py-3 text-xs">{l.assigned_employee_name || <span className="text-amber-400">Unassigned</span>}</td>
                      <td className="px-3 py-3 text-xs text-muted-foreground">{l.next_followup_at ? fmtDate(l.next_followup_at) : "—"}</td>
                      <td className="px-3 py-3 text-xs text-muted-foreground">{fmtDate(l.created_at)}</td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" data-testid={`assign-lead-${l.id}`} onClick={() => { setAssignLead(l); setAssignTo(l.assigned_employee_id || ""); }} className="text-blue-400"><UserCog className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  );
                })}
                {data.items.length === 0 && <tr><td colSpan={10} className="px-4 py-12 text-center text-muted-foreground">No leads found for this view.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {!data ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> :
          data.items.map((l) => {
            const sm = statuses.find((s) => s.slug === l.current_status);
            return (
              <div key={l.id} data-testid={`lead-card-${l.id}`} className="glass rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggleSel(l.id)} data-testid={`select-lead-${l.id}`} className="mt-1" />
                  <div className="flex-1" onClick={() => navigate(`/admin/leads/${l.id}`)}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-primary">{l.lead_code}</span>
                    </div>
                    <div className="font-semibold mt-1 flex items-center gap-1.5">{l.name}{l.is_dnc && <ShieldOff className="h-3 w-3 text-red-400" />}</div>
                    <div className="text-xs text-muted-foreground font-mono">{l.mobile}</div>
                    {l.latest_remark && <div data-testid={`lead-remark-${l.id}`} className="text-[11px] text-slate-400 mt-1 line-clamp-2">Remark: {l.latest_remark}</div>}
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {sm && <StatusBadge status={sm.name} colorToken={sm.color_token} />}
                      <span className="text-xs text-muted-foreground">{l.assigned_employee_name || "Unassigned"}</span>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" data-testid={`assign-lead-${l.id}`} onClick={() => { setAssignLead(l); setAssignTo(l.assigned_employee_id || ""); }} className="text-blue-400"><UserCog className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        {data && data.items.length === 0 && <p className="text-center text-muted-foreground py-10">No leads found.</p>}
      </div>

      {/* Pagination */}
      {data && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Show</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPage(1); setPageSize(Number(v)); }}>
              <SelectTrigger data-testid="page-size-select" className="h-8 w-[88px] bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {[15, 50, 100, 500].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">per page · {data.total ?? 0} total</span>
          </div>
          {data.total_pages > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Page {data.page} of {data.total_pages}</span>
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="prev-page" className="border-slate-700"><ChevronLeft className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" disabled={page >= data.total_pages} onClick={() => setPage(page + 1)} data-testid="next-page" className="border-slate-700"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
      )}

      {/* Sticky bulk action bar */}
      {selected.size > 0 && (
        <div data-testid="bulk-bar" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 glass rounded-full border border-slate-700 shadow-2xl px-3 py-2 flex items-center gap-2">
          <span className="text-xs font-semibold px-2">{selected.size} selected</span>
          <Button size="sm" data-testid="bulk-assign" onClick={() => setBulk({ action: "assign", target: "" })} className="rounded-full bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 h-8"><Users className="h-3.5 w-3.5 mr-1" />Assign</Button>
          <Button size="sm" data-testid="bulk-status" onClick={() => setBulk({ action: "status", target: "" })} className="rounded-full bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 h-8"><Tag className="h-3.5 w-3.5 mr-1" />Status</Button>
          <Button size="sm" data-testid="bulk-dnc" onClick={() => setBulk({ action: "dnc" })} className="rounded-full bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 h-8"><ShieldOff className="h-3.5 w-3.5 mr-1" />DNC</Button>
          <Button size="sm" data-testid="bulk-archive" onClick={() => setBulk({ action: "archive" })} className="rounded-full bg-slate-700 text-slate-200 hover:bg-slate-600 h-8"><Archive className="h-3.5 w-3.5 mr-1" />Archive</Button>
          <button onClick={() => setSelected(new Set())} className="text-muted-foreground hover:text-foreground px-1"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Assign dialog */}
      <Dialog open={!!assignLead} onOpenChange={(o) => !o && setAssignLead(null)}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader><DialogTitle>Assign Lead {assignLead?.lead_code || ""}</DialogTitle>
            <DialogDescription className="text-muted-foreground">Assign this lead to a telecaller, or leave empty for round-robin.</DialogDescription>
          </DialogHeader>
          <Label className="text-xs text-muted-foreground">Choose telecaller (empty = round-robin)</Label>
          <Select value={assignTo} onValueChange={setAssignTo}>
            <SelectTrigger data-testid="assign-select" className="bg-slate-900/60 border-slate-700"><SelectValue placeholder="Round-robin (auto)" /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.assignment_status}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter><Button onClick={doAssign} data-testid="confirm-assign-button" className="bg-primary text-primary-foreground">Assign</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk confirm dialog */}
      <Dialog open={!!bulk} onOpenChange={(o) => !o && setBulk(null)}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader><DialogTitle>{bulkTitle}</DialogTitle>
            <DialogDescription className="text-muted-foreground">Applied to all selected leads and recorded in each timeline and the audit log.</DialogDescription>
          </DialogHeader>
          {bulk?.action === "assign" && (
            <Select value={bulk.target} onValueChange={(v) => setBulk({ ...bulk, target: v })}>
              <SelectTrigger data-testid="bulk-assign-select" className="bg-slate-900/60 border-slate-700"><SelectValue placeholder="Round-robin (auto)" /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {bulk?.action === "status" && (
            <Select value={bulk.target} onValueChange={(v) => setBulk({ ...bulk, target: v })}>
              <SelectTrigger data-testid="bulk-status-select" className="bg-slate-900/60 border-slate-700"><SelectValue placeholder="Choose status" /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 max-h-72">{statuses.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <p className="text-xs text-muted-foreground">This action is applied to all selected leads and recorded in each lead's timeline & the audit log.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulk(null)} className="border-slate-700">Cancel</Button>
            <Button onClick={runBulk} disabled={busy || (["assign", "status"].includes(bulk?.action) && !bulk?.target)} data-testid="confirm-bulk-button" className="bg-primary text-primary-foreground">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
